import { addToChainAfter, addToChainBefore } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import displayProducts, {
    chain as displayProductsChain,
    GetUnfilteredProducts,
    PopulateApiParameter,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import qs from "qs";

/**
 * The selected clearance product ids are intentionally kept OUT of the URL. A clearance category can expand to
 * hundreds or thousands of product ids, and serializing that list into the query string produces URLs that are
 * not just ugly but long enough to hit browser/server URL-length limits. Instead we hold the ids in this
 * module-level cache for the lifetime of the session and inject them straight onto the product-list api
 * parameter on every load.
 *
 * Trade-off (chosen deliberately): the filter is session-only. It does not survive a hard refresh and is not
 * encoded in shareable/bookmarkable links. It does survive in-app navigation, because the cache is reused as
 * long as the product list is still showing the same clearance category.
 *
 * The cache is scoped to the clearance category id (supplied by the widget from its category context) so that
 * navigating to any other product list never inherits the clearance product-id filter.
 */
let activeClearance: { categoryId: string; productIds: string[] } = { categoryId: "", productIds: [] };

// Flip to false to silence the temporary diagnostics once the feature is verified.
const CLEARANCE_DEBUG = false;

/** The clearance list can identify itself through any of these, depending on how the page is set up. */
const getLoadCategoryId = (props: any): string | undefined =>
    props.result?.catalogPage?.categoryId ||
    props.apiParameter?.categoryId ||
    props.result?.productFilters?.pageCategoryId;

/**
 * Injects the cached clearance product ids directly onto the api parameter - not onto `productFilters`, which
 * is what `SetFilterQuery` serializes into the URL. Running immediately after the core `PopulateApiParameter`
 * means the downstream `DispatchSetParameter` (which feeds `state.parameter`, read by the filter widgets) and
 * `DispatchLoadProducts` both see the ids, while the URL stays clean. Scoped by category id so it only ever
 * affects the clearance list.
 */
const InjectClearanceProductIds = (props: any) => {
    if (!activeClearance.productIds.length) {
        return;
    }

    const loadCategoryId = getLoadCategoryId(props);
    const matches = !!loadCategoryId && loadCategoryId === activeClearance.categoryId;

    if (CLEARANCE_DEBUG) {
        logger.warn(
            `[Clearance] inject check load=${loadCategoryId} active=${activeClearance.categoryId} ids=${activeClearance.productIds.length} match=${matches}`,
        );
    }

    if (matches) {
        props.apiParameter = { ...props.apiParameter, productIds: activeClearance.productIds };
    }
};

/**
 * The core `GetUnfilteredProducts` builds the "unfiltered" comparison query by copying the api parameter and
 * deleting the known filter keys (brandIds, attributeValueIds, priceFilters, ...) - but it never deletes
 * `productIds`, so the unfiltered facet counts would otherwise be filtered by the same id list. We drop
 * `productIds` here so the unfiltered query collapses to "the clearance category, unfiltered", which is
 * identical across toggles and therefore served from cache after the first load.
 */
const StripClearanceProductIdsFromUnfiltered = (props: any) => {
    if (props.apiParameter?.productIds?.length) {
        const { productIds, ...rest } = props.apiParameter;
        props.apiParameter = rest;
    }
};

let chainExtended = false;

export const ensureClearanceProductIdsChain = () => {
    if (!chainExtended) {
        addToChainAfter(displayProductsChain as any, PopulateApiParameter as any, InjectClearanceProductIds);
        addToChainBefore(
            displayProductsChain as any,
            GetUnfilteredProducts as any,
            StripClearanceProductIdsFromUnfiltered,
        );
        chainExtended = true;
    }
};

// Extend the chain as soon as this module is imported (the Clearance filter widgets import it).
ensureClearanceProductIdsChain();

/**
 * Applies the supplied clearance product ids as a session-only product-list filter. The ids are cached in
 * module state (scoped to the supplied clearance category id) and the product list is reloaded directly via
 * `displayProducts` - deliberately NOT through `setFilterQuery`, so nothing is written to the URL. Pagination
 * is reset by dropping `page` from the query string the reload reparses. Passing an empty array clears the
 * filter.
 */
export const applyClearanceProductIds =
    (productIds: string[], clearanceCategoryId?: string) =>
    (dispatch: any, getState: () => ApplicationState) => {
        const productListState = getState().pages.productList;
        const categoryId = clearanceCategoryId ?? productListState.productFilters.pageCategoryId ?? "";

        activeClearance = { categoryId, productIds };

        if (CLEARANCE_DEBUG) {
            logger.warn(`[Clearance] apply catId=${categoryId} ids=${productIds.length}`);
        }

        const currentSearch = typeof window !== "undefined" ? window.location.search : "";
        const currentPath = typeof window !== "undefined" ? window.location.pathname : "";

        // Reset to the first page whenever the selection changes (mirrors the core filter widgets).
        const parsed = qs.parse(currentSearch.startsWith("?") ? currentSearch.substr(1) : currentSearch);
        delete parsed.page;
        const queryString = qs.stringify(parsed);

        dispatch(
            displayProducts({
                path: currentPath,
                queryString,
                isSearch: productListState.isSearchPage ?? false,
                relevancy: true,
            }) as any,
        );
    };
